import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { List } from 'immutable';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { billerAccountsSelectors } from 'app-components/flux/biller-accounts';
import { categoriesSelectors } from 'app-components/flux/categories';

import {
  chartOfAccountsSelectors,
  chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  customFrequencyUtils,
  scheduledTransactionsActions,
  scheduledTransactionsTypes,
} from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import { FrequencyEnum } from 'app-components/flux/scheduled-transactions/frequency';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import CloseIcon from '@mui/icons-material/ClearRounded';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import QButton from 'components/QButton';

import BasicTab from './tabs/basicTab';
import BillConnectTab from './tabs/billConnectTab';
import FrequencyTab from './tabs/frequencyTab';

import { matchCriteriaOptions } from './types';
import type { FormikScheduledTxn, MatchOptions, STFormikProps } from './types';

const useStyles = makeStyles()((theme: Theme) => ({
  tabs: {
    display: 'flex',
    flexShrink: 0,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
  },
  tabPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    minHeight: '548px',
    borderBottom: `1px solid ${theme.palette.border.main}`,
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
    },
  },
  billConnect: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  hidden: {
    display: 'none',
  },
  frequencyHeader: {
    display: 'flex',
    padding: '14px 24px',
    backgroundColor: theme.palette.background.default,
  },
  titleText: {
    display: 'flex',
    justifySelf: 'center',
    fontSize: 24,
    fontWeight: '400',
    flexGrow: 1,
    justifyContent: 'center',
    paddingLeft: '40',
  },
  closeButton: {
    display: 'flex',
    justifySelf: 'flex-end',
    color: theme.palette.text.secondary,
    flexGrow: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
}));

const getInitialDate = (model: any, txn?: any, currentDate?: DateTime) => {
  const dueOnDate = model?.dueOn && DateTime.fromISO(model.dueOn);
  if (dueOnDate?.isValid) {
    return dueOnDate;
  }
  const txnDate = txn?.postedOn && DateTime.fromISO(txn.postedOn);
  if (txnDate?.isValid) {
    return txnDate;
  }
  return currentDate;
};

const getInitialAmount = (scheduledTransaction: ScheduledTransactionType) => {
  let criteria: MatchOptions;
  if (scheduledTransaction?.matchAnyAmount) criteria = matchCriteriaOptions.ANY_AMOUNT;
  else if (
    scheduledTransaction?.matchAmountVariance &&
    scheduledTransaction?.matchAmountVariance > 0 &&
    !scheduledTransaction?.resetMatchAmountVariance
  )
    criteria = matchCriteriaOptions.LIMITED_RANGE;
  else if (scheduledTransaction?.matchAmountVariance === 0 && !scheduledTransaction?.resetMatchAmountVariance)
    criteria = matchCriteriaOptions.EXACT_AMOUNT;
  else criteria = matchCriteriaOptions.AUTO_MATCH;

  return criteria;
};

// ===== Tab Panel =====
type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};
function TabPanel(props: TabPanelProps) {
  const { classes } = useStyles();
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      className={value === index ? classes.tabPanel : classes.hidden}
    >
      {value === index && children}
    </div>
  );
}

// ===== Form Content =====
interface FormProps {
  formikProps: STFormikProps;
  create?: boolean;
  scheduledTransaction: scheduledTransactionsTypes.ScheduledTransactionType;
  minimumDate?: DateTime;
  subscriptionInitial?: boolean;
  tabValue: number;
  setTabValue: (_: number) => void;
  dialogActions: any;
}
function FormContent({
  formikProps,
  create,
  scheduledTransaction,
  minimumDate,
  subscriptionInitial,
  tabValue,
  setTabValue,
  dialogActions,
}: FormProps) {
  const { classes } = useStyles();

  const { transaction } = (formikProps?.values || {}) as { transaction: TransactionProps };

  const fiServEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'billsFiserv'));
  const creditAccount = useAppSelector((state) => transactionsSelectors.getCreditAccountFromTxn(state, transaction));
  const canBVA = Boolean(creditAccount && transactionsUtils.isTransferTxn(transaction));

  // TO DO: NEEDS TO BE ADJUSTED FOR FISERV DATA (e.g. checking billerAccounts)
  const canFiserv = Boolean(fiServEnabled);

  const handleChange = (_e: unknown, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Form className={classes.form}>
      <Tabs value={tabValue} onChange={handleChange} aria-label='custom frequency tabs' className={classes.tabs}>
        <Tab label='Basic Details' />
        <Tab label='Frequency & Occurrence' />
        {(canBVA || canFiserv) && <Tab label='Bill Connect' />}
      </Tabs>

      {/* Basic Details */}
      <TabPanel value={tabValue} index={0}>
        <DialogContent id='frm-root-basic'>
          <BasicTab
            formikProps={formikProps}
            scheduledTransaction={scheduledTransaction}
            setTabValue={setTabValue}
            create={create}
            subscriptionInitial={subscriptionInitial}
            canBVA={canBVA}
          />
        </DialogContent>
      </TabPanel>

      {/* Frequency & Occurrence */}
      <TabPanel value={tabValue} index={1}>
        <DialogContent id='frm-root-frequency'>
          <FrequencyTab formikProps={formikProps} minimumDate={minimumDate} />
        </DialogContent>
      </TabPanel>

      {/* Bill Connect */}
      {(canBVA || canFiserv) && (
        <TabPanel value={tabValue} index={2}>
          <DialogContent className={classes.billConnect} id='frm-root-bill-connect'>
            <BillConnectTab BVA={canBVA} fiServ={canFiserv} formikProps={formikProps} creditAccount={creditAccount} />
          </DialogContent>
        </TabPanel>
      )}
      {dialogActions}
    </Form>
  );
}

export interface ScheduledTransactionEditDialogProps {
  open: boolean;
  title?: string;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  onClose: (isDirty?: boolean, forceClose?: boolean) => void;
  onSubmit?: () => void;
  onBack?: () => void;
  subscriptionInitial?: boolean;
  incomeInitial?: boolean;
  transferInitial?: boolean;
  fakeCreate?: boolean;
  initialTab?: number;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

function ScheduledTransactionEditDialog({
  open,
  title = 'Edit series',
  scheduledTransaction: scheduledTransactionProp,
  onClose, // accepts props 'dirty', and 'forceClose' in order
  onSubmit,
  onBack,
  subscriptionInitial = false,
  incomeInitial = false,
  transferInitial = false,
  fakeCreate,
  initialTab = 0,
}: ScheduledTransactionEditDialogProps) {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const model = scheduledTransactionProp;
  const scheduledTransaction = (model && 'toJS' in model ? model.toJS() : model) as ScheduledTransactionType; // .toJS() turns all properties to unknown

  const create = fakeCreate || !model?.id;

  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const initialDate = getInitialDate(scheduledTransaction, model.transaction, currentDate);
  const minimumDate = DateTime.min(initialDate, currentDate);

  const source = window.location.pathname.replaceAll?.('/', '');

  // @ts-expect-error FIXME TS2345
  const isDrawer = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('webDrawerInput'));
  const connected = model?.billPresentmentAccountId && model?.billPresentmentAccountId !== '0';
  const billConnected = useAppSelector((state) =>
    Boolean(connected && billerAccountsSelectors.getBillerAccountForId(state, model?.billPresentmentAccountId)),
  );
  const incomeCat = useAppSelector((state) =>
    incomeInitial ? categoriesSelectors.getPersonalIncomeCategory(state) : null,
  );
  const [didTrySubmit, setDidTrySubmit] = useState(false);

  const [tabValue, setTabValue] = useState(initialTab);

  // # ===================================================================================== #
  // # -----                               Formik Setup                                ----- #
  // # ===================================================================================== #

  const initialValues: FormikScheduledTxn = useMemo(() => {
    const incomeRecurrence: RecurrenceType = {
      byDay: customFrequencyUtils.indexToWeekday(currentDate.weekday - 1),
      frequency: FrequencyEnum.WEEKLY,
      interval: 2,
    };

    const billRecurrence: RecurrenceType = {
      byMonthDay: [currentDate.day],
      frequency: FrequencyEnum.MONTHLY,
      interval: 1,
    };

    // Sanity Check
    let isExcludedFromF2S = scheduledTransaction?.transaction?.isExcludedFromF2S;
    let isExcludedFromReports = scheduledTransaction?.transaction?.isExcludedFromReports;
    const excludeF2SDest =
      create ||
      (scheduledTransaction &&
        (scheduledTransaction?.excludeF2S === scheduledTransactionsTypes?.STExcludeEnum?.BOTH ||
          scheduledTransaction?.excludeF2S === scheduledTransactionsTypes?.STExcludeEnum?.DEST));
    const excludeReportsDest =
      create ||
      (scheduledTransaction &&
        (scheduledTransaction?.excludeReports === scheduledTransactionsTypes?.STExcludeEnum?.BOTH ||
          scheduledTransaction?.excludeReports === scheduledTransactionsTypes?.STExcludeEnum?.DEST));

    let defaultCoa =
      scheduledTransaction.transaction?.coa ||
      chartOfAccountsTypes.mkChartOfAccount({ id: '0', type: 'UNCATEGORIZED' });
    if (incomeInitial && incomeCat && incomeCat.id && create) {
      defaultCoa = { id: incomeCat.id, type: 'CATEGORY' };
    }
    if (transferInitial && create) {
      defaultCoa = { id: '2', type: 'BALANCE_ADJUSTMENT' };
      isExcludedFromReports = true;
      isExcludedFromF2S = true;
    }

    const state = store.getState();
    const spreadTxn =
      scheduledTransaction.transaction && 'toJS' in scheduledTransaction.transaction
        ? (scheduledTransaction.transaction.toJS() as TransactionProps) // .toJS() turns all properties to unknown
        : scheduledTransaction.transaction;

    const isCredit = chartOfAccountsSelectors.isCreditBill(state, spreadTxn?.accountId, defaultCoa);

    // Is transaction of type 'transfer'
    const isTransfer = chartOfAccountsUtils.isTransferCOA(defaultCoa) || isCredit;
    const isIncome = categoriesSelectors.isIncomeCat(state, defaultCoa.id);
    const defaultRecurrence = scheduledTransaction.recurrence || (isIncome ? incomeRecurrence : billRecurrence);

    const frequencyAlias = customFrequencyUtils.mapRecurrenceFrequencyToFrequencyAlias(defaultRecurrence);
    const endOnDate =
      scheduledTransaction.recurrence?.endOn &&
      (scheduledTransaction.recurrence.endOn instanceof DateTime
        ? scheduledTransaction.recurrence.endOn
        : DateTime.fromISO(scheduledTransaction.recurrence.endOn));
    const virtualEndOn = endOnDate && endOnDate.isValid ? endOnDate : null;
    const { autoAdjustAmount, autoAdjustDueOn } = scheduledTransaction;

    const BPSFields = billConnected
      ? {
          autoAdjustAmount: autoAdjustAmount || scheduledTransactionsTypes.AutoAdjustAmountEnum.none,
          autoAdjustDueOn: Boolean(autoAdjustDueOn),
        }
      : {};

    const immutableTags =
      scheduledTransaction.transaction && scheduledTransaction.transaction.tags
        ? List(scheduledTransaction.transaction?.tags)
        : undefined;

    const formikScheduledTransaction = {
      // all modifiable props should be listed below (formik requires it)
      ...scheduledTransaction,
      ...BPSFields,
      dueOn: initialDate,
      transaction: {
        amount: 0,
        payee: '',
        coa: defaultCoa,
        postedOn: initialDate.toISODate(),
        ...spreadTxn,
        accountId: spreadTxn?.accountId || '',
        isExcludedFromF2S,
        isExcludedFromReports,
        excludeF2SDest,
        excludeReportsDest,
        isSubscription: Boolean(spreadTxn?.isSubscription),
        isBill: Boolean(!spreadTxn?.isSubscription && !isIncome),
        isTransfer,
        //  isCredit,
        tags: immutableTags,
      },
      virtualFrequency: frequencyAlias,
      virtualEndOn,
      virtualRecurrence: defaultRecurrence,
      matchCriteria: getInitialAmount(scheduledTransaction),
      autoAdjustAmount: scheduledTransaction.autoAdjustAmount || AutoAdjustAmountEnum.none,
      autoAdjustDueOn: Boolean(create ? false : scheduledTransaction.autoAdjustDueOn),
    };
    return formikScheduledTransaction;
  }, [
    currentDate,
    scheduledTransaction,
    create,
    incomeInitial,
    transferInitial,
    incomeCat,
    store,
    billConnected,
    initialDate,
  ]);

  // TODO: Move to bit.dev in future during cleanup work?
  // @ts-expect-error FIXME TS7006
  const getExclusions = (values) => {
    let excludeF2S: string = scheduledTransactionsTypes?.STExcludeEnum?.NONE;
    let excludeReports: string = scheduledTransactionsTypes?.STExcludeEnum?.NONE;

    if (values?.coa?.type === 'ACCOUNT') {
      if (values.isExcludedFromF2S && values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.BOTH;
      } else if (!values.isExcludedFromF2S && values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.DEST;
      } else if (values.isExcludedFromF2S && !values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }

      if (values.isExcludedFromReports && values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.BOTH;
      } else if (!values.isExcludedFromReports && values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.DEST;
      } else if (values.isExcludedFromReports && !values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }
    } else {
      if (values.isExcludedFromF2S) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }

      if (values.isExcludedFromReports) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }
    }

    return {
      excludeF2S,
      excludeReports,
    };
  };

  const getType = (values: TransactionProps) => {
    let type: string = 'income';
    if (values.isBill) type = 'bill';
    if (values.isSubscription) type = 'subscription';
    if (values.isTransfer) type = 'transfer';

    return type;
  };

  const formikSubmit = (values: any, actions: any) => {
    const {
      virtualRecurrence,
      dueOn,
      virtualEndOn,
      virtualFrequency,
      transaction,
      id,
      matchCriteria,
    }: FormikScheduledTxn = values || {};
    const isValidRecurrence = customFrequencyUtils.isValidRecurrence(virtualRecurrence);

    // The following set of if conditions specifically checks reqiured field values on submit.  Ideally this would be done via form field validation, but since
    // fields are on different tabs, Formik does not handle this well.  In the future, we should convert this entire form to react-hook-form and react-hook-form-mui
    const errs = { virtualRecurrence: '', dueOn: '', virtualEndOn: '' };
    if (!isValidRecurrence) {
      errs.virtualRecurrence = 'Required';
    }

    const startDateMissingMessage = !dueOn ? 'Required' : '';
    if (startDateMissingMessage.length > 0) {
      errs.dueOn = startDateMissingMessage;
    }

    const minDueOn = DateTime.min(initialValues.dueOn, currentDate);

    if (dueOn < minDueOn) {
      errs.dueOn = `Date must be between ${minDueOn.toFormat('D')}`;
    }

    if (virtualEndOn && virtualEndOn < dueOn) {
      errs.virtualEndOn = `Date must be on or after ${dueOn.toFormat('D')}`;
    }

    // if there are any errors on the given fields, set those errors and switch the correct tab.
    if (errs.virtualRecurrence !== '' || errs.dueOn !== '' || errs.virtualEndOn !== '') {
      actions.setErrors(errs);
      actions.setSubmitting(false);
      setTabValue(1);
      return;
    }

    if (customFrequencyUtils.verifyValues(values)) {
      // both the below dates are formatted in such a way because if you send an ISO QCS sometimes won't generate models
      // for same-day because it treats them as in the past depending on the time of day
      const stDueOn = dueOn?.isValid ? dueOn.toFormat('yyyy-MM-dd') : undefined;
      // const endOn = virtualEndOn?.isValid && virtualEndOn.toFormat('yyyy-MM-dd');

      const valTxn = { ...(model?.transaction || {}), ...(transaction || {}) } as TransactionProps;

      // For create or edit series flow, ensure that we do not send invalid value of businessId field during QCS call
      if (valTxn.businessId === 'PERSONAL') {
        valTxn.businessId = '';
      }

      const count = virtualFrequency === 'One-time payment' ? 1 : undefined;

      const state = store.getState();
      const isCredit = valTxn && chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, valTxn.coa);
      const isIncome = transactionsSelectors.isIncomeTxn(state, valTxn);

      // Is transaction of type 'transfer'
      const isTransfer = chartOfAccountsUtils.isTransferCOA(valTxn.coa) || isCredit;

      if (isIncome) {
        valTxn.isTransfer = false;
        valTxn.isBill = false;
        valTxn.isSubscription = false;
      } else if (isTransfer) {
        valTxn.isTransfer = true;
        valTxn.isBill = false;
        valTxn.isSubscription = false;
      }
      const { excludeF2S, excludeReports } = getExclusions(valTxn);
      const type = getType(valTxn);

      const newTxn = transactionsUtils.updateNewTags(transactionsTypes.mkCashFlowTransaction({ ...valTxn }), true);
      const newRecurrence = customFrequencyUtils.recurrenceFromVirtualRecurrence(virtualRecurrence);
      const newRecurrenceRecord = scheduledTransactionsTypes.mkRecurrence({
        ...newRecurrence,
        endOn: virtualEndOn,
        count,
      });

      if (id) {
        dispatch(
          scheduledTransactionsActions.updateScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransaction({
              ...values,
              excludeF2S,
              excludeReports,
              dueOn: stDueOn,
              type: scheduledTransactionsTypes.STTypeEnum.UNKNOWN, // to avoid conflicts like BILL + isSubscription conflict
              recurrence: newRecurrenceRecord,
              transaction: newTxn,
              ...(fakeCreate && { isUserVerified: true }),
            }),
            {
              ...(fakeCreate ? {} : { undo: { userMessage: 'Recurring transaction updated.' } }),
            },
          ),
        );
      } else {
        dispatch(
          scheduledTransactionsActions.createScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransaction({
              clientId: uuidv4().toUpperCase(), // backup for irresponsible developers
              ...values,
              excludeF2S,
              excludeReports,
              dueOn: stDueOn,
              type: scheduledTransactionsTypes.STTypeEnum.UNKNOWN, // to avoid conflict like BILL + isSubscription conflict
              isUserVerified: true,
              recurrence: newRecurrenceRecord,
              transaction: newTxn,
            }),
            { undo: { userMessage: 'Recurring transaction created.' } },
          ),
        );
      }
      const frequency = newRecurrence.alias?.toLowerCase().replaceAll?.('_', ' ') || 'Unknown';
      tracker.track(tracker.events.changeSeries, {
        source,
        purpose: create ? 'create' : 'edit',
        frequency,
        frequencyDetails: newRecurrence,
        type: type,
        frequencyState: newRecurrence?.alias ? 'changed' : 'default',
        matchCriteria: matchCriteria?.toLowerCase(),
        excludeFromSpendingPlan: transaction?.isExcludedFromF2S,
        excludeFromReports: transaction?.isExcludedFromReports,
      });
      actions.setSubmitting(false);
      if (onSubmit) {
        onSubmit();
      } else {
        onClose(false);
      }
    } else {
      transaction && actions.setErrors(customFrequencyUtils.setErrorFields(transaction as TransactionProps));
      setTabValue(0);
    }
  };

  // # ===================================================================================== #
  // # -----                            Action Functions                               ----- #
  // # ===================================================================================== #
  const deleteFunction = () => {
    dispatch(
      scheduledTransactionsActions.deleteScheduledTransaction(
        scheduledTransactionsTypes.mkScheduledTransaction({
          clientId: model?.clientId,
          id: model?.id,
          isDeleted: true,
        }),
        { undo: { userMessage: 'Recurring transaction deleted.' } },
      ),
    );
    onClose(false, true);
  };

  return (
    <Formik<FormikScheduledTxn>
      key={model?.id || model?.clientId}
      initialValues={initialValues}
      validateOnChange={didTrySubmit}
      validateOnBlur={false}
      onSubmit={formikSubmit}
    >
      {(formikProps) => {
        const { dirty } = formikProps || {};

        const formContent = (
          <FormContent
            formikProps={formikProps}
            scheduledTransaction={scheduledTransaction}
            minimumDate={minimumDate}
            subscriptionInitial={subscriptionInitial}
            tabValue={tabValue}
            setTabValue={setTabValue}
            dialogActions={
              // buttons must be inside Form to function properly
              <DialogActions>
                <QButton
                  id='delete-recurrence'
                  variant={create ? 'outlined' : 'delete'}
                  onClick={create ? () => onClose(dirty) : deleteFunction}
                  disableOutline
                >
                  {create ? 'Cancel' : 'Delete'}
                </QButton>
                <QButton
                  id='create-update-recurrence'
                  type='submit'
                  variant='contained'
                  onClick={() => setDidTrySubmit(true)}
                >
                  {create ? 'Create' : 'Update'}
                </QButton>
              </DialogActions>
            }
            create={create}
          />
        );

        if (isDrawer) {
          return (
            <Drawer
              aria-labelledby='edit-recurrence-dialog'
              open={open}
              anchor='right'
              onClose={() => onClose(dirty)}
              PaperProps={{
                sx: { width: '50%' },
              }}
            >
              <div className={classes.frequencyHeader}>
                <div className={classes.titleText}>{title}</div>
                <div className={classes.closeButton}>
                  <IconButton aria-label='Close' id='dlg-close' onClick={() => onClose(false)}>
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
              {formContent}
            </Drawer>
          );
        }

        return (
          <StdDialog
            aria-labelledby='edit-recurrence-dialog'
            onBack={onBack}
            onClose={() => onClose(dirty)}
            open={open}
            showBackButton={Boolean(onBack)}
            size='md'
            title={title}
          >
            <Dump obj={formikProps} />
            {formContent}
          </StdDialog>
        );
      }}
    </Formik>
  );
}

export default ScheduledTransactionEditDialog;
